/**
 * Skeleton loaders — animated placeholder UI that mirrors the final layout.
 *
 * Why: the previous Pro Project flow showed a tiny spinner during 30-90s
 * generation. With these skeletons, the user sees the eventual layout fade in
 * piece-by-piece, dramatically reducing perceived wait time.
 *
 * All variants share the same `AnimatedBlock` primitive and respect prefers-reduced-motion.
 */
import { motion } from 'framer-motion';

const shimmer = {
  animate: {
    backgroundPosition: ['200% 0', '-200% 0'],
  },
  transition: {
    duration: 1.8,
    repeat: Infinity,
    ease: 'linear',
  },
};

const baseClass = 'relative overflow-hidden bg-white/[0.04] border border-white/5';
const shimmerStyle = {
  backgroundImage:
    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.05) 20%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.05) 80%, transparent 100%)',
  backgroundSize: '200% 100%',
};

export const SkeletonBox = ({ className = '', height = 'h-4' }) => (
  <motion.div
    className={`${baseClass} ${height} ${className}`}
    style={shimmerStyle}
    {...shimmer}
    aria-hidden="true"
  />
);

export const SkeletonText = ({ lines = 3, className = '' }) => (
  <div className={`space-y-2 ${className}`} data-testid="skeleton-text">
    {Array.from({ length: lines }).map((_, i) => (
      <SkeletonBox key={i} className={i === lines - 1 ? 'w-2/3' : 'w-full'} height="h-3" />
    ))}
  </div>
);

/** Pro Project skeleton — full layout shown during 30-90s generation */
export const ProProjectSkeleton = ({ language = 'en' }) => (
  <div className="space-y-6 p-6 border border-white/10 bg-[#0A0D1A]" data-testid="pro-project-skeleton">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-1 h-1 bg-cyan-400 rounded-full animate-pulse" />
      <div className="w-1 h-1 bg-cyan-400/60 rounded-full animate-pulse [animation-delay:200ms]" />
      <div className="w-1 h-1 bg-cyan-400/30 rounded-full animate-pulse [animation-delay:400ms]" />
      <span className="text-[10px] font-mono uppercase tracking-widest text-cyan-300/70 ml-2">
        {language === 'tr' ? 'AI tasarımınızı oluşturuyor…' : 'AI is crafting your design…'}
      </span>
    </div>

    {/* Title placeholder */}
    <SkeletonBox className="w-3/4 max-w-md" height="h-8" />
    <SkeletonBox className="w-1/2 max-w-sm" height="h-5" />

    {/* 4-view image grid placeholder */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
      {['front', 'side', 'top', 'iso'].map((view, i) => (
        <div key={view} className="border border-white/5 bg-[#040409]">
          <motion.div
            className="aspect-square overflow-hidden"
            style={shimmerStyle}
            animate={shimmer.animate}
            transition={{ ...shimmer.transition, delay: i * 0.15 }}
          />
          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/30">
            {view}
          </div>
        </div>
      ))}
    </div>

    {/* Specs grid placeholder */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="border border-white/10 p-4 space-y-2">
          <SkeletonBox className="w-1/3" height="h-2" />
          <SkeletonBox className="w-3/4" height="h-5" />
        </div>
      ))}
    </div>

    {/* Description placeholder */}
    <SkeletonText lines={4} className="pt-2" />
  </div>
);

/** Project card skeleton — used in the public gallery grid */
export const ProjectCardSkeleton = () => (
  <div className="border border-white/10 bg-[#0A0D1A] overflow-hidden" data-testid="project-card-skeleton">
    <SkeletonBox className="w-full" height="h-48" />
    <div className="p-4 space-y-3">
      <SkeletonBox className="w-2/3" height="h-5" />
      <SkeletonBox className="w-1/3" height="h-3" />
    </div>
  </div>
);

/** Marketplace listing skeleton */
export const MarketplaceCardSkeleton = () => (
  <div className="border border-white/10 bg-[#0A0D1A] p-5 space-y-4" data-testid="marketplace-card-skeleton">
    <div className="flex items-center justify-between">
      <SkeletonBox className="w-1/2" height="h-6" />
      <SkeletonBox className="w-16" height="h-6" />
    </div>
    <SkeletonText lines={2} />
    <div className="flex items-center justify-between pt-2">
      <SkeletonBox className="w-20" height="h-8" />
      <SkeletonBox className="w-24" height="h-9" />
    </div>
  </div>
);
